import React, { useCallback, useEffect, useState } from "react";
import { Card, Select, Space, Typography, Empty, Alert, Tag } from "antd";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import request from "../utils/request";
import RunTraceTimeline from "../components/agents/RunTraceTimeline";
import PlanGate from "../componets/common/PlanGate";

const { Title, Paragraph } = Typography;

interface AgentItem {
  id: number;
  name: string;
}

/**
 * AgentAudit Agent 操作审计页(PRD v6 §5「数据与增长 / Agent 操作审计」,Pro Max+)。
 * 选择项目 + Agent 后,复用 RunTraceTimeline 呈现该 Agent 的运行记录与逐步 Trace
 * (读 /api/v3/agents/:id/runs 与 /api/v3/agent-runs/:runId,可经 ?runId= 深链)。
 */
const AgentAudit: React.FC = () => {
  const { API_BASE_URL } = useGlobal();
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [agentId, setAgentId] = useState<number | undefined>();

  const loadAgents = useCallback(
    (pid: number) => {
      request
        .get(`${API_BASE_URL}/api/v3/agents`, { params: { page: 1, pageSize: 50, project_id: pid } })
        .then((res) => {
          const items: AgentItem[] = res.data?.data?.items || [];
          setAgents(items);
          setAgentId(items.length > 0 ? items[0].id : undefined);
        })
        .catch(() => {
          setAgents([]);
          setAgentId(undefined);
        });
    },
    [API_BASE_URL]
  );

  useEffect(() => {
    if (projectId) loadAgents(projectId);
  }, [projectId, loadAgents]);

  return (
    <div style={{ padding: 16 }}>
      <Title level={4}>Agent 操作审计</Title>
      <Paragraph type="secondary">
        按项目与 Agent 查看其运行记录与逐步 Trace(操作区域、读取数据、模型与耗时),可经 runId/会话串联追溯。
      </Paragraph>

      <PlanGate
        feature="Agent 操作审计"
        requiredPlan="pro_max"
        description="Agent 操作审计为 Pro Max 及以上套餐能力,升级后可查看完整的 Agent 运行与操作明细。"
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <span>项目</span>
            <Tag color="blue">{project?.name || "—"}</Tag>
            <span>Agent</span>
            <Select
              style={{ minWidth: 220 }}
              value={agentId}
              onChange={setAgentId}
              placeholder="选择 Agent"
              options={agents.map((a) => ({ value: a.id, label: a.name }))}
              notFoundContent="该项目暂无 Agent"
            />
          </Space>
        </Card>

        {agentId ? (
          <RunTraceTimeline agentId={agentId} />
        ) : (
          <Empty description="请选择项目与 Agent 以查看操作审计" />
        )}

        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="审计数据来源:agent_runs 运行记录 + agent_run_steps 步骤轨迹;站点操作明细见站点分析。"
        />
      </PlanGate>
    </div>
  );
};

export default AgentAudit;
